import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Switch, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import UndoOutlined from '@mui/icons-material/UndoOutlined';
import useWatchlistReviewIdentity from './watchlist_table/useWatchlistReviewIdentity';
import { getAppStyleTokens } from '../styles/msiStyleTokens';
import { getCountryAvailabilityKeys, getCountryChipDisplay, renderCountryChipLabel } from './shared/securityDisplayPrimitives';

function securityLabel(group) {
    return [group.current_security?.ticker, group.current_security?.country]
        .filter(Boolean).join(' · ') || 'Security';
}

function semanticTone(theme, tone) {
    const role = tone === 'error' || tone === 'remove' ? 'danger' : tone;
    return getAppStyleTokens(theme).semantics[role];
}

function CurrentCoverage({ coverage, selectedFactorVendor, label }) {
    if (coverage?.state === 'unverified') return <Typography variant="caption">Review listing</Typography>;
    const coverageVendor = String(coverage?.provider || '').trim().toLowerCase();
    if (coverageVendor && selectedFactorVendor && coverageVendor !== selectedFactorVendor) {
        return <Tooltip title="Coverage will appear when your selected factor source has finished updating." arrow>
            <Typography variant="caption" color="text.secondary">Updating coverage…</Typography>
        </Tooltip>;
    }
    const active = coverage?.state === 'direct' ? 'direct' : 'none';
    const option = {
        direct: { label: 'Factor data', tone: 'success' },
        none: { label: 'None', tone: 'error' },
    }[active];
    return <Chip size="small" variant="outlined" label={option.label}
        aria-label={`Current factor coverage for ${label}: ${option.label}`} sx={{
            height: 24, borderRadius: 0.75,
            borderColor: (theme) => semanticTone(theme, option.tone).border,
            backgroundColor: (theme) => semanticTone(theme, option.tone).background,
            color: (theme) => semanticTone(theme, option.tone).foreground,
            '& .MuiChip-label': { px: 0.8, fontSize: '0.7rem', fontWeight: 600 },
        }} />;
}

function CoverageHelp() {
    const [open, setOpen] = useState(false);
    return <>
        <Tooltip title="About current coverage" arrow>
            <IconButton size="small" aria-label="About current coverage" aria-haspopup="dialog" onClick={() => setOpen(true)} sx={{ ml: 0.35, p: 0.25, width: 22, height: 22, border: 0, backgroundColor: 'transparent' }}>
                <InfoOutlined sx={{ fontSize: 15 }} />
            </IconButton>
        </Tooltip>
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth aria-labelledby="factor-coverage-help-title">
            <DialogTitle id="factor-coverage-help-title">Current coverage</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1.5 }}>This status shows the data currently used by your published Watchlist for the selected factor source.</Typography>
                <Stack spacing={1}>
                    <Typography variant="body2"><strong>Factor data:</strong> validated data from your selected factor source.</Typography>
                    <Typography variant="body2"><strong>None:</strong> no usable factor data.</Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 1.5 }}>The match switch starts with the system’s decision or your saved choice. A match still requires verified factor data. Securities without it are excluded; estimates are not used. Editing the match state does not change coverage until you apply and successfully validate the change.</Typography>
            </DialogContent>
            <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
        </Dialog>
    </>;
}

function CountryText({ country }) {
    const display = getCountryChipDisplay(country);
    return <Tooltip title={display.tooltip} arrow><Box component="span">{renderCountryChipLabel(display)}</Box></Tooltip>;
}

function ProposedIdentity({ group, proposal, name }) {
    const ticker = proposal.provider_security?.ticker || proposal.listing_security?.ticker;
    const country = proposal.listing_security?.country;
    const same = (left, right) => String(left || '').trim().toUpperCase() === String(right || '').trim().toUpperCase();
    const differentTicker = ticker && !same(ticker, group.current_security?.ticker);
    const currentCountryKeys = getCountryAvailabilityKeys(group.current_security?.country);
    const differentCountry = country && !getCountryAvailabilityKeys(country).some((key) => currentCountryKeys.includes(key));
    return <>
        <Box component="span">{name}</Box>
        {(differentTicker || differentCountry) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {differentTicker && <>Listing ticker: {ticker}</>}
            {differentTicker && differentCountry && ' · '}
            {differentCountry && <CountryText country={country} />}
        </Typography>}
    </>;
}

function DecisionSwitch({ label, value, onChange, disabled, actionAvailable }) {
    const matched = value === 'accept';
    return <FormControlLabel
        control={<Switch size="small" checked={matched}
            disabled={disabled || !actionAvailable}
            onChange={(_event, checked) => onChange(checked ? 'accept' : 'reject')}
            inputProps={{ role: 'switch', 'aria-label': `Match state for ${label}` }} />}
        label={matched ? 'Matched' : 'Not matched'}
        sx={{ m: 0, gap: 0.5, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }} />;
}

function RemoveSecurityButton({ label, selected, disabled, unavailable, onClick }) {
    const tooltipTitle = disabled
        ? 'A factor review update is currently in progress.'
        : unavailable
            ? 'Removal is unavailable for this review.'
            : selected ? 'Undo staged removal' : 'Stage removal from Watchlist';
    const accessibleLabel = `${selected ? 'Undo staged removal' : 'Stage removal from Watchlist'} for ${label}`;
    return <Tooltip title={tooltipTitle} arrow>
        <span>
            <IconButton size="small" color="default" disabled={disabled || unavailable}
                onClick={onClick} aria-label={accessibleLabel} aria-pressed={selected} sx={{
                    width: 28, height: 28,
                    color: disabled || unavailable ? 'text.disabled' : selected ? 'error.main' : 'text.secondary',
                    '&:hover': { color: disabled || unavailable ? 'text.disabled' : 'error.main' },
                }}>
                {selected ? <UndoOutlined fontSize="small" /> : <DeleteOutline fontSize="small" />}
            </IconButton>
        </span>
    </Tooltip>;
}

// The backend groups equivalent provider records and supplies their signed
// capabilities. Names and tickers are display data, never grouping authority.
export default function ProviderIdentityBulkReview({
    groups, selectedFactorVendor, expectedRevisionId, onPreview, onApply, onRefresh, disabled = false, processing = false,
}) {
    const removalIdentity = useWatchlistReviewIdentity();
    const [choices, setChoices] = useState({});
    const [removals, setRemovals] = useState({});
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const [error, setError] = useState('');
    const [refreshWarning, setRefreshWarning] = useState('');
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const [confirmedPortfolioRemoval, setConfirmedPortfolioRemoval] = useState(false);
    const attemptRef = useRef(null);
    const decisions = useMemo(() => [
        ...Object.entries(removals).filter(([, removal]) => removal).map(([securityKey, removal]) => {
            const group = groups.find((item) => item.security_key === securityKey);
            return {
                security_key: securityKey,
                action: 'remove',
                choice_tokens: [],
                delete_token: group?.removal_evidence_key === removal.evidenceKey
                    ? group.remove_capability?.delete_token : removal.token,
                confirmed_portfolio_removal: false,
            };
        }),
        ...Object.entries(choices).filter(([, selected]) => selected && !removals[selected.securityKey])
            .map(([proposalKey, selected]) => {
                const proposal = groups.find((item) => item.security_key === selected.securityKey)
                    ?.proposals.find((item) => item.proposal_key === proposalKey);
                return {
                    security_key: selected.securityKey,
                    proposal_key: proposalKey,
                    action: selected.action,
                    choice_tokens: proposal?.evidence_key === selected.evidenceKey
                        ? proposal.choice_tokens : selected.tokens,
                };
            }),
    ], [choices, groups, removals]);
    const staleDecisions = decisions.filter((decision) => {
        const group = groups.find((item) => item.security_key === decision.security_key);
        if (!group) return true;
        if (decision.action === 'remove') return !group.remove_capability?.delete_token || !group.removal_evidence_key
            || removals[decision.security_key].evidenceKey !== group.removal_evidence_key;
        const proposal = group.proposals.find((item) => item.proposal_key === decision.proposal_key);
        return !proposal?.evidence_key || proposal.action_available === false
            || choices[decision.proposal_key].evidenceKey !== proposal.evidence_key;
    });
    const stale = staleDecisions.length > 0;
    const unavailableRef = useRef(false);
    unavailableRef.current = stale || disabled;

    const edit = () => {
        setPreview(null);
        setError('');
        setRefreshWarning('');
        setConfirmedPortfolioRemoval(false);
        attemptRef.current = null;
    };
    const selectDecision = (group, proposal, action) => {
        edit();
        setChoices((current) => {
            const remaining = { ...current };
            if (action === 'remove') {
                Object.entries(remaining).forEach(([key, selected]) => {
                    if (selected?.securityKey === group.security_key) delete remaining[key];
                });
                return remaining;
            }
            if (!action || action === (proposal.current_state === 'accept' ? 'accept' : 'reject')) {
                delete remaining[proposal.proposal_key];
                return remaining;
            }
            return {
                ...remaining,
                [proposal.proposal_key]: {
                    action,
                    tokens: proposal.choice_tokens,
                    evidenceKey: proposal.evidence_key,
                    securityKey: group.security_key,
                },
            };
        });
        setRemovals((current) => ({
            ...current,
            [group.security_key]: action === 'remove' ? {
                token: group.remove_capability?.delete_token,
                evidenceKey: group.removal_evidence_key,
            } : null,
        }));
    };
    const refresh = async () => {
        try {
            await onRefresh?.();
            setRefreshWarning('');
        } catch (err) {
            setError(err.message || 'The latest reviews could not be loaded. Your selections have been kept.');
        }
    };
    const apply = async (confirmRemoval = false) => {
        if (busyRef.current || !decisions.length || unavailableRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setError('');
        try {
            if (!attemptRef.current) {
                const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID() : `provider-review-${Date.now()}-${Math.random().toString(16).slice(2)}`;
                attemptRef.current = { id, review: null };
            }
            const attempt = attemptRef.current;
            const hasRemoval = decisions.some((decision) => decision.action === 'remove');
            if (!attempt.review) {
                const response = hasRemoval ? await onPreview(decisions, attempt.id) : {};
                if (!mountedRef.current || unavailableRef.current) return;
                attempt.review = { expected_watchlist_revision_id: expectedRevisionId, ...response, decisions };
            }
            const review = attempt.review;
            // Match edits apply directly. Removal retains its complete impact
            // confirmation because one submitted security can have held siblings.
            if (hasRemoval && !confirmRemoval) {
                setPreview(review);
                return;
            }
            if (review.summary?.portfolio_removed_count > 0 && !confirmedPortfolioRemoval) return;
            const response = await onApply({ ...review, decisions: review.decisions.map((decision) => decision.action === 'remove'
                ? { ...decision, confirmed_portfolio_removal: confirmedPortfolioRemoval } : decision) }, attempt.id);
            if (!mountedRef.current) return;
            setChoices({});
            setRemovals({});
            setPreview(null);
            attemptRef.current = null;
            setConfirmedPortfolioRemoval(false);
            setRefreshWarning(response?.refreshWarning || '');
        } catch (err) {
            if (mountedRef.current) setError(err.message || 'These choices could not be applied. Your selections have been kept.');
        } finally {
            busyRef.current = false;
            if (mountedRef.current) setBusy(false);
        }
    };

    if (!groups.length && !decisions.length && !refreshWarning && !processing) return null;
    return <Box data-testid="provider-identity-bulk-review" sx={{ display: 'grid', gap: 1.25 }}>
        <Box sx={{ px: 0.25 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Factor data reviews</Typography>
                        <Typography variant="caption" color="text.secondary">{groups.length} {groups.length === 1 ? 'security' : 'securities'}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Compare your security’s name with the factor provider’s name. Match choices apply only to your Watchlist; factor coverage is checked when you apply them.
                    </Typography>
                </Box>
                {decisions.length > 0 && <Typography variant="caption" color="primary">{decisions.length} changed</Typography>}
            </Stack>
        </Box>
        {processing && <Alert severity="info">Validating your match choices. Current coverage will update when validation completes.</Alert>}
        {refreshWarning && <Alert severity="warning" action={onRefresh && <Button color="inherit" onClick={refresh} disabled={busy}>Refresh reviews</Button>}>{refreshWarning}</Alert>}
        {error && !preview && <Alert severity="error" action={onRefresh && <Button color="inherit" onClick={refresh} disabled={busy}>Refresh reviews</Button>}>{error}</Alert>}
        {stale && <Alert severity="warning" action={<Button color="inherit" disabled={busy} onClick={() => {
            edit();
            setChoices((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !staleDecisions.some((decision) => decision.proposal_key === key))));
            setRemovals((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !staleDecisions.some((decision) => decision.action === 'remove' && decision.security_key === key))));
        }}>Clear outdated choices</Button>}>A selected review changed. Choose that action again before continuing. Your other selections have been kept.</Alert>}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflowX: 'auto' }}>
            <Table size="small" aria-label="Factor provider reviews" sx={{
                minWidth: 920,
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                    px: 1,
                    py: 0.6,
                    fontSize: '0.75rem',
                    borderRight: (theme) => `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                    verticalAlign: 'middle',
                },
                '& .MuiTableCell-root:last-of-type': { borderRight: 0 },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    py: 0.8,
                    backgroundColor: 'background.default',
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 750,
                    letterSpacing: '0.045em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                },
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 76 }}>Ticker</TableCell>
                        <TableCell sx={{ width: 76 }}>Country</TableCell>
                        <TableCell>Current company name</TableCell>
                        <TableCell>Proposed company name</TableCell>
                        <TableCell sx={{ width: 145 }}>Match state</TableCell>
                        <TableCell sx={{ width: 175 }}>Current coverage<CoverageHelp /></TableCell>
                        <TableCell aria-label="Remove from Watchlist" sx={{ width: 48 }} />
                    </TableRow>
                </TableHead>
                {groups.map((group, groupIndex) => {
                    const removal = removals[group.security_key];
                    const proposals = group.proposals || [];
                    const hasMatchEdit = !removal && proposals.some((proposal) => {
                        const selected = choices[proposal.proposal_key];
                        return selected && selected.evidenceKey === proposal.evidence_key;
                    });
                    return <TableBody key={group.security_key} data-testid="provider-review-security" sx={{
                        backgroundColor: (theme) => removal
                            ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.12 : 0.035)
                            : groupIndex % 2
                                ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.055 : 0.022)
                                : 'background.paper',
                    }}>
                        {proposals.map((proposal, proposalIndex) => {
                            const selected = choices[proposal.proposal_key];
                            const changed = selected && selected.evidenceKey !== proposal.evidence_key;
                            const currentState = proposal.current_state === 'accept' ? 'accept' : 'reject';
                            const value = removal || changed ? currentState : selected?.action || currentState;
                            const proposedName = proposal.provider_security?.names?.join(' / ')
                                || proposal.provider_security?.name || 'Name unavailable';
                            return <TableRow key={proposal.proposal_key} aria-label={`${securityLabel(group)} provider comparison`}>
                                {proposalIndex === 0 && <TableCell rowSpan={proposals.length}>{group.current_security?.ticker || '—'}</TableCell>}
                                {proposalIndex === 0 && <TableCell rowSpan={proposals.length}><CountryText country={group.current_security?.country} /></TableCell>}
                                <TableCell>{proposal.listing_security?.name || group.current_security?.security_name || 'Name unavailable'}</TableCell>
                                <TableCell>
                                    <ProposedIdentity group={group} proposal={proposal} name={proposedName} />
                                </TableCell>
                                <TableCell>
                                    {proposal.reference_only ? <Typography variant="caption">{proposal.guidance}</Typography> : <DecisionSwitch label={`${securityLabel(group)}: ${proposedName}`} value={value}
                                        disabled={busy || disabled || Boolean(removal)}
                                        actionAvailable={proposal.action_available !== false}
                                        onChange={(action) => selectDecision(group, proposal, action)} />}
                                    {proposal.match_origin === 'automatic' && currentState === 'accept' && value === 'accept' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
                                            Matched automatically; company names differ
                                        </Typography>
                                    )}
                                </TableCell>
                                {proposalIndex === 0 && <TableCell rowSpan={proposals.length}>
                                    <CurrentCoverage coverage={group.coverage} selectedFactorVendor={selectedFactorVendor} label={securityLabel(group)} />
                                    {(processing || hasMatchEdit) && <Typography variant="caption" color="text.secondary" aria-live="polite" sx={{ display: 'block', mt: 0.5 }}>
                                        {processing ? 'Validating new coverage…' : 'Apply to validate your change'}
                                    </Typography>}
                                </TableCell>}
                                {proposalIndex === 0 && <TableCell rowSpan={proposals.length} align="center">
                                    <RemoveSecurityButton label={securityLabel(group)} selected={Boolean(removal)}
                                        disabled={busy || disabled}
                                        unavailable={!group.remove_capability?.delete_token}
                                        onClick={() => selectDecision(group, proposal, removal ? '' : 'remove')} />
                                </TableCell>}
                            </TableRow>;
                        })}
                    </TableBody>;
                })}
            </Table>
        </TableContainer>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1}>
            <Typography variant="caption" color="text.secondary">
                Changes take effect after you apply them and validation completes.
            </Typography>
            <Button variant="contained" disabled={busy || disabled || stale || !decisions.length} onClick={() => apply()}
                startIcon={busy && !preview ? <CircularProgress size={16} /> : null}>
                Apply changes{decisions.length ? ` (${decisions.length})` : ''}
            </Button>
        </Stack>
        <Dialog open={Boolean(preview)} onClose={busy ? undefined : () => { setPreview(null); setError(''); }} maxWidth="md" fullWidth aria-labelledby="factor-review-preview-title">
            <DialogTitle id="factor-review-preview-title" sx={{ pb: 1 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 850 }}>Confirm Watchlist removals</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    These removals will be applied together with any match changes you selected.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 1 }} action={onRefresh && <Button color="inherit" onClick={refresh} disabled={busy}>Refresh reviews</Button>}>{error}</Alert>}
                {stale && <Alert severity="warning" sx={{ mb: 1 }}>A selected review changed. Return to your choices to review the updated evidence.</Alert>}
                {preview?.removal_impacts?.length > 0 && <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Watchlist removals ({preview.removal_impacts.length})</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        All listings belonging to the selected submitted securities are removed together. The complete removal list is shown below.
                    </Typography>
                    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small" aria-label="Securities to remove">
                            <TableHead><TableRow><TableCell>{removalIdentity.header}</TableCell><TableCell>Country</TableCell><TableCell>Removed from</TableCell></TableRow></TableHead>
                            <TableBody>{preview.removal_impacts.map((security) => <TableRow key={security.security_key}>
                                <TableCell>{removalIdentity.renderIdentity({ ...security, company_name: security.security_name })}</TableCell>
                                <TableCell>{security.country || '—'}</TableCell>
                                <TableCell>{security.in_actual_portfolio ? 'Watchlist and Actual Portfolio' : 'Watchlist only'}</TableCell>
                            </TableRow>)}</TableBody>
                        </Table>
                    </TableContainer>
                    {removalIdentity.selector}
                </Box>}
                {preview?.summary?.portfolio_removed_count > 0 && <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">{preview.summary.portfolio_removed_count} Actual Portfolio {preview.summary.portfolio_removed_count === 1 ? 'holding will' : 'holdings will'} also be removed.</Typography>
                    <FormControlLabel control={<Checkbox checked={confirmedPortfolioRemoval} disabled={busy} onChange={(event) => setConfirmedPortfolioRemoval(event.target.checked)} />} label="I confirm removing these Actual Portfolio holdings" />
                </Alert>}
            </DialogContent>
            <DialogActions><Button disabled={busy} onClick={() => { setPreview(null); setError(''); }}>Back to choices</Button>
                <Button variant="contained" disabled={busy || stale || (preview?.summary?.portfolio_removed_count > 0 && !confirmedPortfolioRemoval)} onClick={() => apply(true)} startIcon={busy ? <CircularProgress size={16} /> : null}>Apply changes</Button></DialogActions>
        </Dialog>
    </Box>;
}
